import type { ElementType } from 'react';
import type { ArrayPath, Control, FieldArray, FieldValues, Path } from 'react-hook-form';

import { Button } from '@pesto/ui/components/Button';
import { Card } from '@pesto/ui/components/Card';
import { DobField } from '@pesto/ui/components/Forms/DobField';
import { SsnField } from '@pesto/ui/components/Forms/SsnField';
import { PhoneInput } from '@pesto/ui/components/PhoneInput';
import { Typography } from '@pesto/ui/components/Typography';
import { FormInput } from '@pesto/ui/features/Form/FormInput';
import { FormSelect } from '@pesto/ui/features/Form/FormSelect';
import { STATES } from '@pesto/utils/constants/states';
import { PlusIcon, Trash2Icon } from 'lucide-react';
import { Controller, useFieldArray } from 'react-hook-form';

import type { ApplicantFormData } from './openAccountSchema';

import { emptyApplicant } from './openAccountSchema';

type ApplicantField = {
  name: keyof ApplicantFormData;
  label: string;
  required?: boolean;
  component?: ElementType;
  options?: Array<{ label: string; value: string }>;
  placeholder?: string;
  helperText?: string;
  className: string;
};

const APPLICANT_FIELDS: ApplicantField[] = [
  { name: 'first_name', label: 'First Name', required: true, className: 'md:col-span-3' },
  { name: 'middle_name', label: 'Middle Name', className: 'md:col-span-3' },
  { name: 'last_name', label: 'Last Name', required: true, className: 'md:col-span-3' },
  {
    name: 'dob',
    label: 'Date of Birth',
    component: DobField,
    required: true,
    placeholder: 'DD-MM-YYYY',
    className: 'md:col-span-3',
  },
  { name: 'email', label: 'Email', required: true, className: 'md:col-span-5' },
  { name: 'phone', label: 'Phone', component: PhoneInput, required: true, className: 'md:col-span-4' },
  {
    name: 'ssn',
    label: 'SSN',
    component: SsnField,
    required: true,
    placeholder: '123-45-6789',
    className: 'md:col-span-3',
  },
  { name: 'address_line1', label: 'Address Line 1', required: true, className: 'md:col-span-6' },
  { name: 'address_line2', label: 'Address Line 2', className: 'md:col-span-6' },
  { name: 'city', label: 'City', required: true, className: 'md:col-span-6' },
  { name: 'state', label: 'State', options: STATES, required: true, className: 'md:col-span-3' },
  { name: 'zip', label: 'ZIP Code', required: true, className: 'md:col-span-3' },
  {
    name: 'card_ref',
    label: 'i2c Card Reference',
    helperText: 'Leave blank to board without a card.',
    className: 'md:col-span-6',
  },
  { name: 'last_four', label: 'Last Four', helperText: 'Required with a reference.', className: 'md:col-span-3' },
];

const CARD_FIELDS: Array<keyof ApplicantFormData> = ['card_ref', 'last_four'];

type ApplicantsForm = FieldValues & { applicants: ApplicantFormData[] };

interface ApplicantsSectionProps<T extends ApplicantsForm> {
  control: Control<T>;
  maxApplicants?: number;
  hideCardFields?: boolean;
  hideSsn?: boolean;
  ssnOptional?: boolean;
}

export const ApplicantsSection = <T extends ApplicantsForm>({
  control,
  maxApplicants,
  hideCardFields = false,
  hideSsn = false,
  ssnOptional = false,
}: ApplicantsSectionProps<T>) => {
  // SAFETY: T extends ApplicantsForm, so `applicants` is an array path of every T.
  const { fields, append, remove } = useFieldArray({ control, name: 'applicants' as ArrayPath<T> });
  const singleApplicant = maxApplicants === 1;

  const applicantFields = APPLICANT_FIELDS.flatMap((field) => {
    if ((hideCardFields && CARD_FIELDS.includes(field.name)) || (hideSsn && field.name === 'ssn')) return [];

    return [ssnOptional && field.name === 'ssn' ? { ...field, required: false } : field];
  });

  return (
    <div className="flex flex-col gap-3">
      {!singleApplicant && <Typography variant="headerSmall">Applicants</Typography>}

      {ssnOptional && (
        <Typography variant="bodySmall" className="text-muted-foreground">
          SSN is optional because KYC is skipped and no card is issued.
        </Typography>
      )}

      {fields.map((field, index) => (
        <Card key={field.id} className="bg-muted/30 flex flex-col gap-4 p-4">
          {!singleApplicant && (
            <div className="flex items-center justify-between">
              <Typography variant="body" className="font-medium">
                Applicant {index + 1}
              </Typography>
              {fields.length > 1 && (
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  aria-label={`Remove applicant ${index + 1}`}
                  onClick={() => remove(index)}
                >
                  <Trash2Icon className="text-destructive h-4 w-4" />
                </Button>
              )}
            </div>
          )}

          <div className="grid grid-cols-1 gap-4 md:grid-cols-12">
            {applicantFields.map((applicantField) => (
              <Controller
                key={applicantField.name}
                control={control}
                // SAFETY: T extends ApplicantsForm and applicantField.name is a key of ApplicantFormData.
                name={`applicants.${index}.${applicantField.name}` as Path<T>}
                render={({ field: { name, value, onChange }, fieldState }) => {
                  // SAFETY: every ApplicantFormData field is a string or unset, so the value at this path is one.
                  const text = (value as string) ?? '';

                  return applicantField.options ? (
                    <FormSelect
                      name={name}
                      label={applicantField.label}
                      options={applicantField.options}
                      value={text}
                      onChange={onChange}
                      error={fieldState.error?.message}
                      required={applicantField.required}
                      wrapperClassName={applicantField.className}
                    />
                  ) : (
                    <FormInput
                      name={name}
                      as={applicantField.component}
                      label={applicantField.label}
                      value={text}
                      onChange={onChange}
                      error={fieldState.error?.message}
                      required={applicantField.required}
                      placeholder={applicantField.placeholder}
                      helperText={applicantField.helperText}
                      wrapperClassName={applicantField.className}
                    />
                  );
                }}
              />
            ))}
          </div>
        </Card>
      ))}

      {fields.length !== maxApplicants && (
        <Button
          type="button"
          variant="ghost"
          className="text-muted-foreground/70 hover:text-foreground border-muted-foreground/20 hover:border-muted-foreground/40 h-8 w-full border border-dashed text-xs font-normal"
          // SAFETY: T's applicants are ApplicantFormData, which emptyApplicant is.
          onClick={() => append(emptyApplicant as FieldArray<T, ArrayPath<T>>)}
        >
          <PlusIcon className="h-3.5 w-3.5" /> Add another applicant
        </Button>
      )}
    </div>
  );
};
